import { DatePickerUncontrolled, useFetch } from "@4uhub/lib4uhub";
import { Autocomplete, Box, MenuItem, Stack, TextField } from "@mui/material";
import { Dispatch, memo, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";
import {
  DASHBOARD_GENERATE_EXECUTION_STATUS,
  TDashboardGenerateExecutionStatus,
} from "../../../../models/dashboard-generate-execution";
import { getDashboardGenerateExecutionPanels } from "../../../../services/dashboardGenerateExecution.service";

export interface IPanelOption {
  id: string;
  name: string;
}

export interface IExecutionsFilterParams {
  Code: string;
  DashboardPanel: IPanelOption | null;
  StartDate: Date | null;
  EndDate: Date | null;
  Status: TDashboardGenerateExecutionStatus | "";
}

interface IExecutionsFilterProps {
  filterParams: IExecutionsFilterParams;
  onFilterParams: Dispatch<SetStateAction<IExecutionsFilterParams>>;
}

const translationPath = "page.dashboardExecutions.";

const ExecutionsFilter = ({
  filterParams,
  onFilterParams,
}: IExecutionsFilterProps) => {
  const { t } = useTranslation();

  const { filter, showCleanBtn, initialState, setFilter, cleanFilter } =
    useFilter(filterParams);

  const [panels, setPanels] = useState<IPanelOption[]>([]);

  const { sendRequest: panelsRequest, loading: loadingPanels } = useFetch(
    getDashboardGenerateExecutionPanels,
  );

  useEffect(() => {
    panelsRequest(undefined).then(({ data, success }) => {
      if (success && data) setPanels(data);
    });
  }, [panelsRequest]);

  const filterHandler = () => {
    onFilterParams(filter);
  };

  const cleanHandler = () => {
    onFilterParams(initialState.current);
    cleanFilter();
  };

  return (
    <Stack flexDirection={"row"} gap={2} alignItems="center">
      <TextField
        size="small"
        type="number"
        sx={{ width: 120 }}
        label={t(translationPath + "code")}
        value={filter.Code}
        onChange={(e) => setFilter({ Code: e.target.value })}
      />
      <Autocomplete
        size="small"
        sx={{ minWidth: 260 }}
        options={panels}
        loading={loadingPanels}
        value={filter.DashboardPanel}
        onChange={(_, value) => setFilter({ DashboardPanel: value })}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField {...params} label={t(translationPath + "panel")} />
        )}
      />
      <Box sx={{ flex: "0 0 160px" }}>
        <DatePickerUncontrolled
          value={filter.StartDate}
          onChange={(date) => setFilter({ StartDate: date })}
          label={`${t(translationPath + "start_date")}`}
        />
      </Box>
      <Box sx={{ flex: "0 0 160px" }}>
        <DatePickerUncontrolled
          value={filter.EndDate}
          onChange={(date) => setFilter({ EndDate: date })}
          label={`${t(translationPath + "end_date")}`}
        />
      </Box>
      <TextField
        select
        size="small"
        sx={{ minWidth: 200 }}
        label={t(translationPath + "status_label")}
        value={filter.Status}
        onChange={(e) =>
          setFilter({
            Status: e.target.value as TDashboardGenerateExecutionStatus | "",
          })
        }
      >
        <MenuItem value="">{t(translationPath + "all_status")}</MenuItem>
        {DASHBOARD_GENERATE_EXECUTION_STATUS.map((status) => (
          <MenuItem key={status} value={status}>
            {t(translationPath + "status." + status)}
          </MenuItem>
        ))}
      </TextField>
      <FiltersActions
        onFilter={filterHandler}
        onClean={cleanHandler}
        showCleanBtn={showCleanBtn}
      />
    </Stack>
  );
};

export default memo(ExecutionsFilter);
